import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Workflow, Send, Loader2, Cpu, GitBranch, Bot, CheckCircle2, AlertTriangle,
  Play, MessageSquare, RotateCcw, Radio, Smartphone, ShieldCheck, ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PriorityBadge, StatusBadge, Card, CardHeader, EmptyState, Label } from "@/components/common";
import { relativeTime } from "@/lib/format";

const PHASES = [
  ["ingesting", "Ingest"], ["decomposed", "Decompose"], ["awaiting_go", "Strategy + GO"],
  ["executing", "Execute"], ["complete", "Consolidate"],
];
const TARGET_STYLE = {
  claude_code: { label: "Claude Code", cls: "bg-brand/10 text-brand border-brand" },
  codex: { label: "Codex", cls: "bg-ink/5 text-ink border-ink" },
  design: { label: "Design Team", cls: "bg-st-info/10 text-st-info border-st-info" },
  field: { label: "Field Crew", cls: "bg-st-warning/10 text-st-warning border-st-warning" },
  n8n: { label: "n8n", cls: "bg-st-success/10 text-st-success border-st-success" },
  internal: { label: "Internal", cls: "bg-surface-muted text-ink-muted border-border" },
};

export default function Orchestration() {
  const { companyParam, companyId, activeCompany } = useApp();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [raw, setRaw] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: queue = [] } = useQuery({
    queryKey: ["action-queue", companyId],
    queryFn: async () => (await api.get("/action-queue", { params: companyParam })).data,
    refetchInterval: 15000,
  });
  const { data: orchestrations = [] } = useQuery({
    queryKey: ["orchestrations", companyId],
    queryFn: async () => (await api.get("/orchestrations", { params: companyParam })).data,
  });
  const activeId = selected || orchestrations[0]?.id;
  const { data: orch } = useQuery({
    queryKey: ["orchestration", activeId],
    queryFn: async () => (await api.get(`/orchestrations/${activeId}`)).data,
    enabled: !!activeId,
  });

  const run = async () => {
    if (!raw.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orchestrations", { raw, company_id: companyId !== "all" ? companyId : null });
      setRaw("");
      setSelected(data.orchestration.id);
      qc.invalidateQueries();
      toast.success("Directive decomposed — review the plan and GO.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const gate = async (decision) => {
    try {
      await api.post(`/orchestrations/${activeId}/gate`, { decision });
      qc.invalidateQueries();
      toast.success(decision === "go" ? "Launched — sessions running." : `Marked ${decision}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canGate = user?.role === "owner" || user?.role === "admin";

  return (
    <div data-testid="orchestration-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted"><Workflow className="w-3.5 h-3.5 text-brand" /> Orchestration Cockpit</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">Direct the work. Approve from anywhere.</h1>
          <p className="text-sm text-ink-muted mt-1">The external orchestrator brain plugs in here. You see the plan, hit GO, and watch sessions — by login or by message.</p>
        </div>
      </div>

      {/* Action queue — what is waiting on you to click */}
      <Card className="mb-4" testId="action-queue-panel">
        <CardHeader title="Waiting On You — Click to Act" icon={ShieldCheck} action={<span className="text-[11px] font-mono text-ink-faint">{queue.length} items</span>} />
        {queue.length === 0 ? <div className="px-4 py-5 text-sm text-ink-muted">Nothing waiting. You're clear.</div> : (
          <div className="divide-y divide-border">
            {queue.map((q) => (
              <div key={q.id} className="flex items-center gap-3 px-4 py-3">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${q.kind === "orchestration" ? "border-brand text-brand" : "border-st-warning text-st-warning"}`}>{q.kind}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">{q.title}</div>
                  <div className="text-[11px] text-ink-faint">{q.client || "—"} · {relativeTime(q.created_at)}</div>
                </div>
                {q.kind === "orchestration"
                  ? <button onClick={() => { setSelected(q.id); }} className="text-xs font-semibold text-brand flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></button>
                  : <a href={`/approvals/${q.id}`} className="text-xs font-semibold text-brand flex items-center gap-1">Review <ArrowRight className="w-3 h-3" /></a>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* New directive */}
      <Card className="mb-4">
        <div className="p-4">
          <Label className="mb-2">New Directive (or paste a voice transcript)</Label>
          <div className="flex gap-2">
            <textarea value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="orchestration-input" rows={2}
              placeholder="e.g. Build the Lakeside design package start to finish, then CPM schedule, shop drawings and sub docs for the field crew…"
              className="flex-1 border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            <button onClick={run} disabled={busy} data-testid="run-orchestration-btn" className="flex items-center gap-2 bg-brand text-white px-5 text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Decompose
            </button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* list */}
        <Card className="h-fit">
          <CardHeader title="Orchestrations" icon={Workflow} />
          <div className="divide-y divide-border">
            {orchestrations.map((o) => (
              <button key={o.id} onClick={() => setSelected(o.id)} data-testid={`orch-item-${o.id}`}
                className={`w-full text-left px-4 py-3 transition-colors ${activeId === o.id ? "bg-brand/5 border-l-2 border-brand" : "border-l-2 border-transparent hover:bg-surface-muted"}`}>
                <div className="text-sm font-semibold text-ink leading-tight truncate">{o.title}</div>
                <div className="flex items-center gap-2 mt-1"><StatusBadge value={o.status} /><span className="text-[10px] font-mono text-ink-faint">{o.decomposed_tasks.length} tasks</span></div>
              </button>
            ))}
            {orchestrations.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">No orchestrations yet.</div>}
          </div>
        </Card>

        {/* detail */}
        <div className="lg:col-span-2 space-y-4">
          {!orch ? <EmptyState icon={Workflow} title="Select an orchestration" /> : (
            <>
              {/* phases */}
              <Card><div className="flex items-center justify-between px-4 py-3">
                {PHASES.map(([key, label], i) => {
                  const order = PHASES.findIndex(([k]) => k === orch.status);
                  const done = i <= order;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <div className="flex flex-col items-center">
                        <div className={`w-6 h-6 flex items-center justify-center text-[11px] font-bold ${done ? "bg-brand text-white" : "bg-surface-muted text-ink-faint"}`}>{i + 1}</div>
                        <span className={`text-[10px] uppercase tracking-wider mt-1 ${done ? "text-ink" : "text-ink-faint"}`}>{label}</span>
                      </div>
                      {i < PHASES.length - 1 && <div className={`w-8 h-px ${done ? "bg-brand" : "bg-border"}`} />}
                    </div>
                  );
                })}
              </div></Card>

              {/* strategy + gate */}
              <Card>
                <CardHeader title="Strategy & Plan" icon={Cpu} />
                <div className="p-4">
                  <p className="text-sm text-ink">{orch.strategy?.summary}</p>
                  <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                    <span className="bg-surface-muted px-2 py-0.5 uppercase tracking-wider text-ink-muted">mode: {orch.strategy?.mode}</span>
                    <span className="bg-surface-muted px-2 py-0.5 uppercase tracking-wider text-ink-muted">primary: {TARGET_STYLE[orch.primary_target]?.label}</span>
                    <span className={`px-2 py-0.5 uppercase tracking-wider ${orch.completeness?.passed ? "bg-st-success/10 text-st-success" : "bg-st-warning/10 text-st-warning"}`}>{orch.completeness?.passed ? "complete" : "gaps"}</span>
                  </div>
                  {orch.knowledge_gaps?.length > 0 && (
                    <div className="mt-3 bg-st-warning/5 border-l-2 border-st-warning p-2 text-xs text-ink">
                      <b>Knowledge to verify:</b> {orch.knowledge_gaps.join("; ")}
                    </div>
                  )}
                  {orch.status === "awaiting_go" && (
                    <div className="flex gap-2 mt-4">
                      {canGate ? (<>
                        <button onClick={() => gate("go")} data-testid="orch-go-btn" className="flex items-center gap-2 bg-st-success text-white px-5 py-2 text-sm font-semibold hover:brightness-110"><Play className="w-4 h-4" /> GO</button>
                        <button onClick={() => gate("discuss")} className="flex items-center gap-1.5 border border-border px-4 py-2 text-sm hover:bg-surface-muted"><MessageSquare className="w-4 h-4" /> Discuss</button>
                        <button onClick={() => gate("modify")} className="flex items-center gap-1.5 border border-st-warning text-st-warning px-4 py-2 text-sm hover:bg-st-warning/10"><RotateCcw className="w-4 h-4" /> Modify</button>
                      </>) : <div className="text-xs text-ink-muted flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Only the Owner can GO.</div>}
                      <div className="ml-auto flex items-center gap-1.5 text-[11px] text-ink-faint"><Smartphone className="w-3.5 h-3.5" /> Also approvable by message relay</div>
                    </div>
                  )}
                  {orch.gate?.decision !== "pending" && <div className="mt-3 text-xs text-ink-muted">Gate: <b className="uppercase">{orch.gate.decision}</b> by {orch.gate.by} · {relativeTime(orch.gate.at)}</div>}
                </div>
              </Card>

              {/* decomposed tasks by target */}
              <Card>
                <CardHeader title="Decomposed Tasks → Routed" icon={GitBranch} />
                <div className="divide-y divide-border">
                  {orch.decomposed_tasks.map((t) => {
                    const ts = TARGET_STYLE[t.target_system] || TARGET_STYLE.internal;
                    return (
                      <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider border px-1.5 py-0.5 ${ts.cls}`}>{ts.label}</span>
                        <span className="text-sm text-ink flex-1">{t.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-ink-faint">{t.type}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* sessions */}
              {orch.sessions?.length > 0 && (
                <Card>
                  <CardHeader title="Sessions" icon={Radio} action={<span className="w-2 h-2 bg-st-success live-dot" />} />
                  <div className="divide-y divide-border">
                    {orch.sessions.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 px-4 py-2.5">
                        <Bot className="w-4 h-4 text-brand shrink-0" />
                        <div className="flex-1"><div className="text-sm font-semibold text-ink">{TARGET_STYLE[s.system]?.label || s.system}</div><div className="text-[11px] text-ink-faint">{s.current_task}</div></div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${s.status === "running" ? "text-st-success" : s.status === "rate_limited" ? "text-st-warning" : "text-ink-faint"}`}>{s.status}</span>
                        <span className="text-[10px] font-mono text-ink-faint">rl {s.rate_limit?.remaining ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
